import { TechnologySelectDto } from '@@types/api.types';
import { BlueLabel } from '@components/shared/blue-label.component';
import { withForm } from '@components/ui/form/form';
import { useGetCategories } from '@queries/category.query';
import { type MissionAssessmentRequestFormData } from '@schemas/mission-assessment-request.schema';
import { technologyParametersStepSchema } from '@schemas/mission-assessment-request.schema';
import { formOptions, useStore } from '@tanstack/react-form';
import { useEffect, useMemo } from 'react';

const missionAssessmentRequestFormOptions = formOptions({
  defaultValues: {} as MissionAssessmentRequestFormData,
});

export const TechnologyParametersStep = withForm({
  ...missionAssessmentRequestFormOptions,
  props: {
    technology: undefined as TechnologySelectDto | undefined,
  },
  render: function Render({ form, technology }) {
    const { data: categories = [] } = useGetCategories();

    const useId = useStore(form.store, (state) => state.values.useId);

    const subcategories = useMemo(() => categories.flatMap((category) => category.subcategories), [categories]);
    const subcategory = useMemo(
      () => subcategories.find((sub) => sub.id === technology?.subcategoryId),
      [subcategories, technology?.subcategoryId],
    );

    const useOptions = useMemo(() => {
      if (!technology) return [];
      return technology.uses?.map((techUse) => ({ value: techUse.useId, label: techUse.name })) || [];
    }, [technology]);

    const domainOptions = useMemo(() => {
      if (!technology || !useId) return [];
      const selectedTechUse = technology.uses?.find((techUse) => techUse.useId === useId);
      return selectedTechUse?.domains?.map((domain) => ({ value: domain.id, label: domain.name })) || [];
    }, [useId, technology]);

    useEffect(() => {
      if (useOptions.length === 1 && useOptions[0].value !== useId) {
        form.setFieldValue('useId', useOptions[0].value);
      }
    }, [form, useOptions, useId]);

    useEffect(() => {
      if (useId && domainOptions.length === 1) {
        form.setFieldValue('domainId', domainOptions[0].value);
      }
    }, [domainOptions, form, useId]);

    if (!technology) {
      return <div />;
    }

    return (
      <div className="space-y-4">
        <div>
          <BlueLabel text={technology.name} />
          <p className="text-gray-500 text-xs mb-4">
            {technology.category.name}
            {subcategory && `, ${subcategory.name}`}
          </p>
        </div>

        <div className="space-y-3">
          <form.AppField
            name="useId"
            validators={{
              onChange: technologyParametersStepSchema.shape.useId,
            }}
            listeners={{
              onChange: ({ value: newUseId }) => {
                if (newUseId !== useId) {
                  form.setFieldValue('domainId', '');
                }
              },
            }}
          >
            {(field) => (
              <field.SelectField
                label="Use"
                required
                disabled={useOptions.length === 0}
                placeholder={useOptions.length > 0 ? 'Select Use' : 'No uses available for this technology'}
                options={useOptions}
              />
            )}
          </form.AppField>

          <form.AppField
            name="domainId"
            validators={{
              onChange: technologyParametersStepSchema.shape.domainId,
            }}
          >
            {(field) => (
              <field.SelectField
                label="Domain"
                required
                disabled={!useId || domainOptions.length === 0}
                placeholder={
                  useId
                    ? domainOptions.length > 0
                      ? 'Select Domain'
                      : 'No domains available for this use'
                    : 'Select a use first'
                }
                options={domainOptions}
              />
            )}
          </form.AppField>
        </div>
      </div>
    );
  },
});
